// Détail & édition d'une notification/tâche (WKF-013). Ouvert au clic sur une
// ligne ou en deep-link (?open=id). Seul endroit pour modifier titre / échéance
// / description / priorité d'une tâche manuelle. Alertes = lecture seule.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { CheckCircle2, Clock, ExternalLink, Forward, RotateCcw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SimpleRichText } from '@/components/ui/simple-rich-text'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useColleagues } from '@/hooks/useAbsences'
import {
    notificationErrorMessage,
    useNotificationDetail,
    useNotificationMutations,
} from '@/hooks/useNotifications'
import type { Notification, Priority } from '@/hooks/useNotifications.types'
import { useAdminTaskDetail } from '@/hooks/useTaskAdmin'
import type { LinkedEntityValue } from '@/lib/linkedEntity'
import { sanitizeRichHtml } from '@/lib/richText'

import { DueDatePicker } from './DueDatePicker'
import { LinkedEntityPicker } from './LinkedEntityPicker'
import { lienDeLaNotification } from './lienDeLaNotification'
import { PriorityChip } from './PriorityChip'
import { PrioritySelect } from './PrioritySelect'
import { QuickSnoozeItems } from './QuickSnoozeMenu'
import { QuickTransferItems } from './QuickTransferMenu'
import { CLOSED_STATUSES, relativeTime } from './notification-config'
import { useLienRefuseAuDestinataire } from './useLienRefuseAuDestinataire'

interface TaskDetailDialogProps {
    notificationId: string | null
    entityId: string | null
    onOpenChange: (open: boolean) => void
    /** Console admin : détail d'une tâche d'autrui → lecture seule (scope entité). */
    admin?: boolean
    /** Fourni par la page : ouvre le transfert (délégation) de la tâche courante.
     *  Absent (ex. console admin) → bouton « Transférer » masqué. */
    onTransfer?: (n: Notification) => void
    /** Fourni par la page/cloche : ouvre le report précis (date + heure).
     *  Absent (ex. console admin) → « Choisir une date… » indisponible. */
    onSnooze?: (n: Notification) => void
}

export function TaskDetailDialog({ notificationId, entityId, onOpenChange, admin = false, onTransfer, onSnooze }: TaskDetailDialogProps) {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { hasPermission, user } = useAuth()
    const { colleagues } = useColleagues(null, admin ? null : entityId)
    const open = notificationId !== null
    // Mode self (fetch + édition possible) OU admin (fetch scope entité, lecture seule).
    const selfDetail = useNotificationDetail(entityId, admin ? null : notificationId)
    const adminDetail = useAdminTaskDetail(entityId, admin ? notificationId : null)
    const n = admin ? adminDetail.data : selfDetail.data
    const isLoading = admin ? adminDetail.isLoading : selfDetail.isLoading
    const { updateTask, treat, reopen } = useNotificationMutations(entityId)

    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [priority, setPriority] = useState<Priority>('NORMAL')
    const [dueDate, setDueDate] = useState<string | null>(null)
    const [linkedValue, setLinkedValue] = useState<LinkedEntityValue | null>(null)
    // Relier à un AO la tâche d'un collègue qui ne le voit pas : refusé (PO n° 66), dit sous le champ.
    const jugement = useLienRefuseAuDestinataire({ entityId, tache: n, lien: linkedValue, moi: user?.id })

    // Ne (ré)initialiser l'état d'édition que lorsque l'IDENTITÉ de la tâche change
    // — un refetch de fond (même id, nouvelle référence) ne doit PAS écraser une
    // saisie en cours. L'éditeur non contrôlé est remonté via key={n.id} plus bas.
    useEffect(() => {
        if (n) {
            setTitle(n.title)
            setMessage(n.message ?? '')
            setPriority(n.priority)
            setDueDate(n.due_date)
            setLinkedValue(lienDeLaNotification(n))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [n?.id])

    const isManual = n?.source_type === 'manual'
    // Éditable si la tâche m'est destinée OU que je l'ai créée/assignée (même en
    // console admin) ; sinon lecture seule. Règle identique au backend update_task.
    const isMineOrAssigner = Boolean(user && n && (n.recipient_id === user.id || n.assigned_by?.id === user.id))
    const editable = Boolean(
        isManual && n?.status !== 'IGNORED' && hasPermission('tasks:update') && isMineOrAssigner,
    )
    // Transfert (délégation) depuis la modale : uniquement si la page fournit le
    // handler, sur une tâche éditable, avec la permission et au moins un collègue.
    const canTransfer = Boolean(
        editable && onTransfer && hasPermission('tasks:assign') && colleagues.length > 0,
    )
    const isClosed = Boolean(n && CLOSED_STATUSES.has(n.status))
    // Sans action posée (alerte de workflow, escalade…) : la FICHE liée selon le registre, comme la puce.
    const href = n?.actions?.[0]?.href || lienDeLaNotification(n)?.href

    // « Ouvrir » mène à la fiche détail REFERMÉ : la cloche reste montée d'une page à l'autre, sa
    // modale couvrirait sinon la fiche. Refermer AVANT de naviguer : /notifications ne retire `?open=`
    // (en remplaçant l'entrée courante, `useDetailOuvertParLUrl`) que si le navigateur montre le détail ;
    // fermé après la navigation, l'entrée `?open=` resterait, et « Précédent » rouvrirait le détail.
    function ouvrirLaFiche(cible: string) {
        onOpenChange(false)
        navigate(cible)
    }

    // Terminer / Rouvrir depuis la modale (le double-clic depuis la cloche ouvre ici :
    // il faut pouvoir clôturer sans passer par la case à cocher — retour PO).
    function toggleDone() {
        if (!n) return
        ;(isClosed ? reopen : treat).mutate(n.id)
        onOpenChange(false)
    }

    async function save() {
        if (!n) return
        if (!title.trim()) {
            toast.error(t('notifications.task.titleRequired', 'Donnez un titre à votre tâche.'))
            return
        }
        try {
            await updateTask.mutateAsync({
                id: n.id,
                payload: {
                    title: title.trim(),
                    // HTML sanitisé (allowlist) avant persistance — pas de stored-XSS.
                    message: sanitizeRichHtml(message) || null,
                    priority,
                    due_date: dueDate,
                    // Fiche liée (WKF-013 §5.4) : null = lien retiré.
                    linked_entity_type: linkedValue?.type ?? null,
                    linked_entity_id: linkedValue?.id ?? null,
                    linked_entity_label: linkedValue?.label ?? null,
                    // `|| null` et non `?? null` : un href VIDE (type absent du registre
                    // de recherche → `linkedEntityHref` rend `null`, remplacé par `''`)
                    // est REFUSÉ par le validateur anti-open-redirect du serveur — 422
                    // avant la route, sans log, et la tâche n'est pas créée. Une chaîne
                    // vide veut dire « pas d'URL », pas « URL invalide ».
                    linked_entity_href: linkedValue?.href || null,
                },
            })
            toast.success(t('notifications.task.saved', 'Tâche mise à jour.'))
            onOpenChange(false)
        } catch (err) {
            toast.error(notificationErrorMessage(err, t))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle>
                        {editable
                            ? t('notifications.detail.editTitle', 'Modifier la tâche')
                            : t('notifications.detail.title', 'Détail')}
                    </DialogTitle>
                </DialogHeader>

                {isLoading || !n ? (
                    <div className="space-y-3 py-2">
                        <Skeleton className="h-9 w-full" />
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-9 w-1/2" />
                    </div>
                ) : editable ? (
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="detail-title">{t('notifications.task.fieldTitle', 'Titre')} *</Label>
                            <Input
                                id="detail-title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={255}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('notifications.task.fieldDescription', 'Description')}</Label>
                            {/* Éditeur non contrôlé : key sur l'id → remonte avec la
                                description de la tâche courante ; onChange tient `message` à jour.
                                defaultValue SANITISÉ (l'innerHTML de l'éditeur est un DOM VIVANT :
                                un message brut &lt;img onerror&gt; s'exécuterait sinon — finding revue). */}
                            <SimpleRichText
                                key={n.id}
                                defaultValue={sanitizeRichHtml(n.message ?? '')}
                                onChange={setMessage}
                                rows={3}
                                placeholder={t(
                                    'notifications.task.descriptionPlaceholder',
                                    'Détails, contexte, points à vérifier…',
                                )}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('notifications.task.due', 'Échéance')}</Label>
                            <DueDatePicker value={dueDate} onChange={setDueDate} />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('notifications.task.priority', 'Priorité')}</Label>
                            <PrioritySelect value={priority} onChange={setPriority} />
                        </div>
                        <LinkedEntityPicker
                            value={linkedValue}
                            onChange={setLinkedValue}
                            entityId={entityId}
                            refus={jugement.refuse ? notificationErrorMessage({ code: 'TASK_ASSIGNEE_OUT_OF_TENDER' }, t) : null}
                        />
                    </div>
                ) : (
                    // Lecture seule (alerte de workflow / escalade / transfert reçu).
                    <div className="space-y-3 py-2">
                        <h3 className="text-lg font-semibold">{n.title}</h3>
                        {n.message && (
                            // HTML de mise en forme sanitisé par allowlist (aucune dépendance ;
                            // seules b/i/u/listes/alignement survivent → pas de stored-XSS).
                            <div
                                className="prose prose-sm max-w-none text-sm text-muted-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                                dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(n.message) }}
                            />
                        )}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <PriorityChip priority={n.priority} showNormal />
                            {n.due_date && <span>{new Date(n.due_date).toLocaleString(i18n.language)}</span>}
                            <span>{relativeTime(n.created_at, i18n.language)}</span>
                            {n.assigned_by && (
                                <span>{t('notifications.assignedBy', 'Assigné par {{name}}', { name: n.assigned_by.name })}</span>
                            )}
                        </div>
                        {href && (
                            <Button variant="outline" size="sm" onClick={() => ouvrirLaFiche(href)}>
                                <ExternalLink className="mr-2 h-4 w-4" />
                                {/* Libellé porté par l'action (« Voir le rapport »…) — repli
                                    générique seulement s'il manque, iso NotificationRow. */}
                                {n.actions?.[0]?.label ||
                                    t('notifications.detail.openSource', 'Ouvrir la fiche liée')}
                            </Button>
                        )}
                    </div>
                )}

                <DialogFooter
                    className={
                        n && (editable || !admin) ? 'sm:justify-between' : undefined
                    }
                >
                    {/* Alerte / notif reçue (lecture seule, la mienne) : mêmes deux
                        actions que la cloche — Reporter (disparaît, réapparaît à la
                        date) et Marquer comme terminé (disparaît). Masqué en console
                        admin (tâche d'autrui) et en mode édition (actions ci-dessous). */}
                    {!editable && n && !admin && (
                        <div className="flex flex-wrap gap-2">
                            {isClosed ? (
                                <Button
                                    variant="outline"
                                    onClick={toggleDone}
                                    disabled={reopen.isPending}
                                >
                                    <RotateCcw className="mr-2 h-4 w-4" />
                                    {t('notifications.actions.reopen', 'Rouvrir')}
                                </Button>
                            ) : (
                                <>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="outline">
                                                <Clock className="mr-2 h-4 w-4" />
                                                {t('notifications.actions.snooze', 'Reporter…')}
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="w-64">
                                            <QuickSnoozeItems
                                                notification={n}
                                                entityId={entityId}
                                                onPickDate={(x) => {
                                                    onSnooze?.(x)
                                                    onOpenChange(false)
                                                }}
                                                onSnoozed={() => onOpenChange(false)}
                                            />
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    <Button
                                        variant="outline"
                                        onClick={toggleDone}
                                        disabled={treat.isPending}
                                    >
                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                        {t('notifications.actions.treat', 'Marquer comme terminé')}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                    {editable && n && (
                        <div className="flex flex-wrap gap-2">
                            {/* Terminer / Rouvrir : action clé accessible directement dans la modale. */}
                            <Button variant="outline" onClick={toggleDone} disabled={treat.isPending || reopen.isPending}>
                                {isClosed ? (
                                    <>
                                        <RotateCcw className="mr-2 h-4 w-4" />
                                        {t('notifications.actions.reopen', 'Rouvrir')}
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                        {t('notifications.actions.treat', 'Marquer comme terminé')}
                                    </>
                                )}
                            </Button>
                            {canTransfer && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="text-muted-foreground">
                                            <Forward className="mr-2 h-4 w-4" />
                                            {t('notifications.actions.transfer', 'Transférer')}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="w-64">
                                        <QuickTransferItems
                                            notification={n}
                                            entityId={entityId}
                                            onMore={(x) => {
                                                onTransfer?.(x)
                                                onOpenChange(false)
                                            }}
                                            onTransferred={() => onOpenChange(false)}
                                        />
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            {editable ? t('common.cancel', 'Annuler') : t('common.close', 'Fermer')}
                        </Button>
                        {editable && (
                            <Button onClick={save} disabled={updateTask.isPending || !title.trim() || jugement.refuse || jugement.enCours}>
                                {t('common.save', 'Enregistrer')}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
